import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore, type LoginResponse } from '@/stores/auth'
import { fetchClientInstanceId } from '@/utils/clientInstance'
import { useUserStore } from '@/stores/user'
import { useMetaStore } from '@/stores/meta'
import { usePartnerStore } from '@/stores/partner'

/**
 * All valid API hosts for this application. Validated at runtime so the fetch
 * destination is explicitly allowlisted even if the env var is misconfigured,
 * preventing SSRF via a tampered build-time variable or unexpected fallback.
 */
const ALLOWED_API_HOST_RE = /^https?:\/\/([\w-]+\.)*goboomtown\.com(:\d+)?$/i

const SSO_USERSTATUS_MAX_ATTEMPTS = 5
const SSO_USERSTATUS_BACKOFF_BASE_MS = 50
const SSO_USERSTATUS_BACKOFF_CAP_MS = 400
/** Fails stuck SSO if ACS or userStatus never resolves (watchdog cancels in-flight base client requests). */
const SSO_COMPLETION_WATCHDOG_MS = 90_000

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isAllowedApiHost(baseUrl: string): boolean {
  try {
    return ALLOWED_API_HOST_RE.test(new URL(baseUrl).origin)
  } catch {
    return false
  }
}

/**
 * ptid is a partner-integration identifier. Restrict to alphanumeric, dash,
 * and underscore so it can never introduce path-traversal segments when
 * interpolated into the ACS URL path.
 */
function isValidPtid(ptid: string): boolean {
  return /^[a-zA-Z0-9_-]{1,64}$/.test(ptid)
}

export interface SSOCheckResponse {
  success: boolean;
  canAuthenticateInternal: boolean;
  canAuthenticateExternal: boolean;
  externalIdPs: Array<{
      logo: string | null;
      name: string;
      description: string;
      url: string;
  }>;
  current_server_time: string;
}

export interface AcsLoginResponse {
  success: boolean
  authenticated?: boolean
  redirectUrl?: string
  current_server_time: string
}

/**
 * Composable for handling SSO/ACS (Single Sign-On/Assertion Consumer Service) login flow
 */
export function useSSO() {
  const router = useRouter()
  const authStore = useAuthStore()
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Check if the user is authenticated via SSO (getUserAuth). When VITE_APP_ENV is 'local' or
   * the app is on a local-env host (e.g. appv5-local-env.goboomtown.com), use same origin when
   * cross-origin so the request goes through the app proxy. For stage, preprod, and prod use
   * VITE_BOOMTOWN_API_HOST.
   */
  const checkSSO = async (email: string): Promise<SSOCheckResponse> => {
    try {
        console.debug('🔍 Auth Store: Checking SSO status for email:', email)

        const formData = new URLSearchParams()
        formData.append('email', email)

        const apiHost = (import.meta.env.VITE_BOOMTOWN_API_HOST as string || '').replace(/\/$/, '')
        const origin = typeof window !== 'undefined' ? window.location.origin : ''
        let baseUrl = apiHost || origin
        if (apiHost && origin) {
          try {
            if (new URL(apiHost).origin !== origin) {
              baseUrl = origin
            }
          } catch {
            baseUrl = origin
          }
        }
        const url = `${baseUrl}/admin/v4/core/?sAction=getUserAuth`

        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'x-boomtown-client-instance-id': fetchClientInstanceId(),
            'x-boomtown-csrf-token': authStore.csrfToken ?? '',
            'x-boomtown-app': 'cxme',
          },
          body: formData.toString(),
        })

        if (!response.ok) {
          const errBody = await response.text()
          console.error('❌ Auth Store: getUserAuth failed', response.status, errBody)
          throw new Error('Failed to check SSO status')
        }

        const data = (await response.json()) as SSOCheckResponse
        console.debug('✅ Auth Store: SSO check completed successfully')
        return data
    } catch (error) {
        console.error('❌ Auth Store: Failed to check SSO status:', error)
        throw new Error('Failed to check SSO status')
    }
  }

  /**
   * Complete SAML ACS preflight: POST to the API host (e.g. api.stage.goboomtown.com) so the
   * backend sets the session cookie (relay, dstoken) with Domain for the API host. The next
   * request (userStatus, etc.) goes to the same host and sends those cookies → authenticated: true.
   * Incorrect: using app origin (app.stage.goboomtown.com or appv5.stage-ovationcxm.app) would
   * set the cookie for the app host, so userStatus to api.stage would not receive it.
   * Uses VITE_BOOMTOWN_API_HOST when set (local, stage, preprod, prod); otherwise window.location.origin.
   * @param preflight - UUID from IdP redirect query
   * @param ptid - Integration id from IdP redirect query
   */
  const submitAcsLogin = async (preflight: string, ptid: string): Promise<AcsLoginResponse> => {
    isLoading.value = true
    error.value = null

    authStore.isCompletingSsoLogin = true
    authStore.isLoggingIn = true

    const ac = new AbortController()
    const watchdogId = window.setTimeout(() => {
      authStore.cancelInFlightAuthRequests()
      ac.abort()
    }, SSO_COMPLETION_WATCHDOG_MS)

    try {
      let apiHost = (import.meta.env.VITE_BOOMTOWN_API_HOST as string || '').replace(/\/$/, '')
      if (apiHost && /^https?:\/\/app\.(stage\.|preprod\.)?goboomtown\.com$/i.test(apiHost)) {
        apiHost = apiHost.replace(/\/\/app\./, '//api.')
      }
      const baseUrl =
        apiHost
          ? apiHost
          : (typeof window !== 'undefined' ? window.location.origin : '')
      if (!isAllowedApiHost(baseUrl)) {
        throw new Error('SSO configuration error: API host is not in the allowed list')
      }
      if (!isValidPtid(ptid)) {
        throw new Error('SSO configuration error: invalid ptid format')
      }

      const acsUrl = `${baseUrl}/admin/v4/sso/saml/${encodeURIComponent(ptid)}/acs?preflight=${encodeURIComponent(preflight)}`
      const response = await fetch(acsUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preflight, ptid }),
        signal: ac.signal,
      })

      if (!response.ok) {
        const errBody = await response.text()
        console.error('❌ AcsLogin: ACS request failed', response.status, errBody)
        throw new Error(`SSO could not be completed: ${response.status}`)
      }

      const data = (await response.json()) as AcsLoginResponse
      console.debug('✅ acs login: completed successfully', data)

      if (data.authenticated !== true) {
        return data
      }

      let sessionData: LoginResponse | null = null
      for (let attempt = 0; attempt < SSO_USERSTATUS_MAX_ATTEMPTS; attempt++) {
        if (ac.signal.aborted) {
          throw new Error(
            'SSO sign-in timed out. Check your connection and try again from the login page.'
          )
        }
        if (attempt > 0) {
          await sleepMs(
            Math.min(
              SSO_USERSTATUS_BACKOFF_BASE_MS * 2 ** (attempt - 1),
              SSO_USERSTATUS_BACKOFF_CAP_MS
            )
          )
        }
        if (ac.signal.aborted) {
          throw new Error(
            'SSO sign-in timed out. Check your connection and try again from the login page.'
          )
        }
        sessionData = await authStore.refreshCsrfToken(true)
        if (
          sessionData?.success &&
          sessionData.authenticated &&
          sessionData.csrf_token
        ) {
          break
        }
        sessionData = null
      }

      if (ac.signal.aborted) {
        throw new Error(
          'SSO sign-in timed out. Check your connection and try again from the login page.'
        )
      }

      if (!sessionData?.authenticated) {
        throw new Error(
          'SSO could not confirm session after sign-in. Please try again from the login page.'
        )
      }

      authStore.isAuthenticated = true
      authStore.isInitialized = true

      sessionStorage.removeItem('redirectAfterTimeoutLogin')

      try {
        const partnerStore = usePartnerStore()
        const metaStore = useMetaStore()
        const userStore = useUserStore()

        if (userStore.userData?.object_id) {
          try {
            await partnerStore.fetchCurrentPartner()
            console.log('🏢 SSO: Successfully fetched current partner data')
          } catch (partnerError) {
            console.error('🏢 SSO: Failed to fetch current partner data:', partnerError)
          }
        }

        try {
          await Promise.all([
            metaStore.loadMetaData(),
            metaStore.loadPartnerMetaData()
          ])
          console.log('📊 SSO: Successfully loaded meta data and partner meta data')
        } catch (metaError) {
          console.error('📊 SSO: Failed to load meta data:', metaError)
        }
      } catch (loadError) {
        console.error('❌ SSO: Failed to load additional data after CSRF refresh:', loadError)
      }

      if (authStore.redirectAfterLogin) {
        if (authStore.redirectAfterLogin.startsWith('https')) {
          window.location.href = authStore.redirectAfterLogin
          authStore.redirectAfterLogin = ''
        } else {
          const isValidRedirect = authStore.redirectAfterLogin !== '/login' &&
            !authStore.redirectAfterLogin.startsWith('/login?') &&
            authStore.redirectAfterLogin !== '/passwordreset' &&
            !authStore.redirectAfterLogin.startsWith('/passwordreset?')

          if (isValidRedirect) {
            try {
              await router.push(authStore.redirectAfterLogin)
              authStore.redirectAfterLogin = ''
            } catch (navError) {
              console.error('❌ SSO: Redirect navigation failed:', {
                redirectPath: authStore.redirectAfterLogin,
                error: navError instanceof Error ? navError.message : 'Unknown error'
              })
              authStore.redirectAfterLogin = ''
              await router.push('/inbox?view=home')
            }
          } else {
            console.log('🏠 useSSO: Invalid redirect path, going to inbox home instead')
            authStore.redirectAfterLogin = ''
            await router.push('/inbox?view=home')
          }
        }
      } else {
        console.log('🏠 useSSO: No saved redirect, going to inbox home')
        await router.push('/inbox?view=home')
      }

      return data
    } catch (err) {
      let errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      if (err instanceof DOMException && err.name === 'AbortError') {
        errorMessage =
          'SSO sign-in timed out. Check your connection and try again from the login page.'
      }
      error.value = errorMessage
      console.error('❌ AcsLogin: ACS login error:', errorMessage)
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new Error(errorMessage)
      }
      throw err
    } finally {
      window.clearTimeout(watchdogId)
      authStore.isLoggingIn = false
      authStore.isCompletingSsoLogin = false
      isLoading.value = false
    }
  }

  return {
    isLoading,
    error,
    submitAcsLogin,
    checkSSO
  }
}
